import React, { useCallback, useState } from 'react';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { SecondaryTextMode, TranslationsComponent } from 'app/components/translation';
import { SpinLoader } from 'app/components';

import { maxLengthText, TextConfigurationTranslation } from '../../constants';
import { TextConfigurationFormState, TextConfigurationItem, TextItemValidation } from '../types-store';

import { TextFormContainer } from './text-form-container';
import { TextFormDetails } from './text-form-details';

interface TextFormRendererProps {
  formState: TextConfigurationFormState;
  mainLanguage: number;
  development: boolean;
  shouldPrompt: boolean;

  deleteText: (textId: number, textSet: number, successMessage: string) => void;
  saveText: (textItem: TextConfigurationItem,
    successMessage: string,
    validationErrorMessage: string) => void;

  onTextSetChange: (value: ComplexValue<number>) => void;
  onTextCollectionChange: (values: ComplexValue<number>[]) => void;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  onTextChange: (languageId: number, text: string) => void;
  copyTextToAllTranslations: (languageId: number, successMessage: string) => void;
  updateValidation: (validation: TextItemValidation) => void;
  displayError: (errorMessage: string) => void;

  translation: TextConfigurationTranslation;
  locale: string;
}

export const TextFormRenderer: React.FC<TextFormRendererProps> = ({
  formState, mainLanguage, development, shouldPrompt, 
  deleteText, saveText,
  onTextSetChange, onTextCollectionChange,
  options, loadOptions,
  onTextChange, copyTextToAllTranslations,
  updateValidation, displayError,
  translation, locale
}) => {

  const { textItem, fetching, languageOptions, validation } = formState;
  const updatingTextItem = isDefinedIncludingZero(textItem?.id);

  const [showTranslations, switchTranslationsToggle] = useState(true);

  const copyTextToAll = useCallback(
    () => {
      copyTextToAllTranslations(mainLanguage, translation.text_copied_to_all);
    },
    [mainLanguage, translation]
  );

  if (!languageOptions || fetching) {
    return <SpinLoader />;
  }

  return <TextFormContainer
    formState={formState}
    mainLanguage={mainLanguage}
    development={development}
    shouldPrompt={shouldPrompt}
    switchTranslationsToggle={switchTranslationsToggle}
    deleteText={deleteText}
    saveText={saveText}
    updateValidation={updateValidation}
    displayError={displayError}
    validation={validation}
    translation={translation} >
    <MainGridContainer>
      <TextFormDetails
        textItem={textItem}
        development={development}
        onTextSetChange={onTextSetChange}
        onTextCollectionChange={onTextCollectionChange}
        options={options}
        loadOptions={loadOptions}
        validation={validation}
        locale={locale}
        translation={translation} />
    </MainGridContainer>
    <TranslationsComponent
      primaryText={textItem?.translations?.map(t => ({ value: t.language, description: t.text }))}
      secondaryText={textItem?.translations?.map(t => ({ value: t.language, description: t.originalText }))}
      primaryTextValueKey="text"
      secondaryTextValueKey="originalText"
      primaryTextMaxLength={maxLengthText}
      secondaryTextMaxLength={undefined}
      languageOptions={languageOptions}
      mainLanguage={mainLanguage}
      secondaryTextMode={updatingTextItem ? SecondaryTextMode.readOnly : SecondaryTextMode.disabled}
      readOnly={false}
      onPrimaryTextChange={onTextChange}
      onSecondaryTextChange={undefined}
      copyPrimaryTextToAllTranslations={copyTextToAll}
      copySecondaryTextToAllTranslations={undefined}
      hasTranslationsToggled={showTranslations}
      switchTranslationsToggle={switchTranslationsToggle}
      validation={validation?.language}
      primaryTextLabel={translation?.text}
      secondaryTextLabel={translation?.original_text}
      copyPrimaryTextToAllLabel={translation?.copy_text_to_all}
      copySecondaryTextToAllLabel={undefined}
      translationsHeaderLabel={translation?.translations_header}
      switchTranslationsToggleLabel={translation?.show_translations}
    />
  </TextFormContainer>;
};
